import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Project modules
import Message from "../../Components/Message/Message";
import Header from "../../Components/Header/Header";
import RoomsIcons from "../../Components/RoomsIcons/RoomsIcons";
import { introSinglePlayRooms, introCompleted } from "../../Config/config";
import { GameContext } from "../../Context/GameContext";
import { RoomStatus } from "../../Types/Game";
import ActicaGroup from "../../Components/Branding/GroupLogos";
import ControlButtons from "../../Components/ControlButtons/ControlButtons";

/**
 * SinglePlaythrough
 * -----------------
 * Entry screen for single-room mode.
 * Behaviour:
 * - Clears the active room on mount and (re)primes the timer baseline for single-room runs.
 * - If there is exactly one active, open room, auto-select it.
 * - Starts the challenge, guarding against no room being selected.
 */

// COMPONENT ---------------------------------------------------------
const SinglePlaythrough: React.FC = () => {
  /** CONTEXT ------------------------------------------------------- */
  const navigate = useNavigate();
  const {
    setStartTime,
    roomStates,
    setCurrentRoom,
    currentRoom,
    singleRoom,
    eventName, // <-- add this so we can pass it to <Header />
  } = useContext(GameContext);

  /** STATE --------------------------------------------------------- */
  const [error, setError] = useState<string>("");

  /** EFFECTS ------------------------------------------------------- */
  useEffect(() => {
    setCurrentRoom("");
    if (singleRoom) {
      setStartTime(Math.floor(Date.now() / 1000));
    }
  }, [setCurrentRoom, singleRoom, setStartTime]);

  useEffect(() => {
    if (!singleRoom || !roomStates) return;
    const openActiveIds = Object.entries(roomStates)
      .filter(([, r]) => r.active && r.status === RoomStatus.Open)
      .map(([id]) => id);
    if (openActiveIds.length === 1) {
      const onlyId = openActiveIds[0];
      if (currentRoom !== onlyId) setCurrentRoom(onlyId);
    }
  }, [singleRoom, roomStates, currentRoom, setCurrentRoom]);

  /** DERIVED ------------------------------------------------------- */
  const allCompleted =
    !!roomStates &&
    Object.values(roomStates)
      .filter((room) => room.active)
      .every(
        (room) =>
          room.status === RoomStatus.Completed ||
          room.status === RoomStatus.Trapped
      );

  /** HANDLERS ------------------------------------------------------ */
  const handleStartClick = () => {
    if (singleRoom && !currentRoom) {
      setError("Select an Escape Room that you would like to play!");
      return;
    }
    setError("");
    setStartTime(Math.floor(Date.now() / 1000));
    navigate("/escaperoom");
  };

  /** RENDER -------------------------------------------------------- */
  return (
    <div className="escape-room">
      <div className="flex-container">
        <ControlButtons />

        <div className="box box-round box-white box-start">
          <Header
            content={
              allCompleted
                ? introCompleted
                : introSinglePlayRooms.map((sentence, index) => {
                    const isLast = index === introSinglePlayRooms.length - 1;
                    return isLast ? (
                      <strong key={index}>{sentence}</strong>
                    ) : (
                      <span key={index}>{sentence}</span>
                    );
                  })
            }
            eventName={eventName}
          />

          {/* No "Select an Escape Room..." text in single-play mode */}
          <RoomsIcons />

          {error && <Message message={error} type="error" />}

          <div className="button-rail">
            {!allCompleted && (
              <button className="btn-blue" onClick={handleStartClick}>
                Start the challenge!
              </button>
            )}
          </div>
        </div>
      </div>

      <ActicaGroup />
    </div>
  );
};

export default SinglePlaythrough;